import { useEffect, useRef, useState } from "react";
import {
  Pin,
  Archive,
  ArchiveRestore,
  Loader2,
  RefreshCw,
  Plus,
} from "lucide-react";
import {
  archiveProject,
  restoreProject,
  projectColorHex,
  Project,
} from "@/lib/api/projects";
import { cn } from "@/lib/utils";

interface Props {
  projects: Project[];
  loading: boolean;
  error: string | null;
  activeProjectId: string | null;
  activeChatId: string | null;
  onProjectSelect: (id: string) => void;
  onCreateClick: () => void;
  /** Data is owned by the parent (single source of truth) — after any
   *  mutation the parent re-fetches. */
  onRefresh: () => void;
  onArchived: (id: string) => void;
  onRestored: (id: string) => void;
}

/**
 * Projects List — the ONLY independently scrolling zone of the sidebar
 * (RAIOS Ch.11.7 / UX contract: header, navigation, footer stay fixed;
 * only this list scrolls, acceptance = 50+ projects).
 *
 * Stateless: the parent owns the data (GET /api/projects, real backend,
 * no mock data) and re-fetches after mutations.
 */
export function ProjectsList({
  projects,
  loading,
  error,
  activeProjectId,
  activeChatId,
  onProjectSelect,
  onCreateClick,
  onRefresh,
  onArchived,
  onRestored,
}: Props) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const setListRef = (el: HTMLDivElement | null) => {
    listRef.current = el;
  };
  // One measured project row: py-1.5 (12px) + text-sm line-height (20px)
  // ≈ 32px → cap target ≈ 10 rows (spec: «примерно 10 видимых Projects»).
  // Measured live from the first rendered item, not hardcoded.
  const [maxH, setMaxH] = useState<number | null>(null);
  useEffect(() => {
    if (loading || error || projects.length === 0) return;
    const li = listRef.current?.querySelector<HTMLLIElement>("li");
    if (!li) return;
    const h = Math.round(li.getBoundingClientRect().height);
    if (h > 20) setMaxH(10 * h + 8); // +8px list gap allowance
  }, [loading, error, projects.length]);

  const handleArchive = async (e: React.MouseEvent, p: Project) => {
    e.stopPropagation();
    try {
      await archiveProject(p.id);
      onArchived(p.id);
    } catch {
      /* keep the list as-is; the item stays visible */
    }
  };

  const handleRestore = async (e: React.MouseEvent, p: Project) => {
    e.stopPropagation();
    try {
      await restoreProject(p.id);
      onRestored(p.id);
    } catch {
      /* ignore */
    }
  };

  // Content-driven height: the list area grows with its items and stops at
  // the cap (~10 rows, measured live below) — after that ONLY the list
  // scrolls. `flex: 0 1 auto` = never grow beyond content, but still
  // shrinkable (min-h-0) on very short viewports so history keeps its floor.
  return (
    <div className="min-h-0 flex flex-col" style={{ flex: "0 1 auto" }} aria-label="Проекты">
      {/* Section header — part of the flex column above the scroll area:
          it never scrolls (only the list below does). */}
      <div className="px-4 pt-3 pb-1 flex items-center justify-between shrink-0">
        <span className="text-[11px] font-semibold text-tertiary uppercase tracking-wider">
          Проекты
        </span>
        <button
          onClick={onCreateClick}
          className="p-1 rounded text-tertiary hover:text-accent transition-colors"
          aria-label="Новый проект"
          title="Новый проект"
        >
          <Plus size={13} />
        </button>
      </div>

      {/* Independent vertical scroll container for projects (sidebar UX
          contract). Height = content, capped at ~10 rows (measured from the
          real item height); after the cap ONLY this list scrolls while
          header/nav/footer and the history zone stay put. */}
      <div
        className="min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-2"
        style={maxH ? { maxHeight: maxH } : undefined}
        ref={setListRef}
        role="list"
        aria-label="Список проектов"
      >
        {loading ? (
          <div className="px-4 py-6 flex flex-col items-center gap-2 text-tertiary">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-xs">Загрузка…</span>
          </div>
        ) : error ? (
          <div className="px-4 py-6 text-center">
            <p className="text-xs text-tertiary">{error}</p>
            <button
              onClick={onRefresh}
              className="mt-2 inline-flex items-center gap-1 text-xs text-accent hover:underline"
            >
              <RefreshCw size={11} /> Повторить
            </button>
          </div>
        ) : projects.length === 0 ? (
          <div className="px-4 py-3 text-center">
            <p className="text-xs text-tertiary">Проектов пока нет</p>
            <button
              onClick={onCreateClick}
              className="mt-2 text-xs text-accent hover:underline"
            >
              Создать первый проект
            </button>
          </div>
        ) : (
          <ul className="space-y-0.5">
            {projects.map((p) => {
              const isActive = activeProjectId === p.id && !activeChatId;
              return (
                <li
                  key={p.id}
                  role="listitem"
                  className="group flex items-center gap-2 pl-2 pr-1.5 py-1.5 mx-1 rounded-lg cursor-pointer transition-colors text-left"
                  tabIndex={0}
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => onProjectSelect(p.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onProjectSelect(p.id);
                    }
                  }}
                >
                  <span
                    className={cn(
                      "shrink-0 w-4 h-4 flex items-center justify-center text-[13px] leading-none",
                      p.is_archived && "opacity-50",
                    )}
                    style={{ color: projectColorHex(p.color) }}
                    aria-hidden
                  >
                    {p.icon || "◆"}
                  </span>
                  <span
                    className={cn(
                      "flex-1 min-w-0 truncate text-sm text-primary",
                      p.is_archived && "text-tertiary",
                    )}
                  >
                    {p.name}
                  </span>
                  {p.is_pinned && (
                    <Pin size={11} className="shrink-0 text-accent" />
                  )}
                  {p.is_archived ? (
                    <button
                      onClick={(e) => void handleRestore(e, p)}
                      className="p-0.5 rounded text-tertiary hover:text-accent transition-colors md:opacity-0 md:group-hover:opacity-100"
                      aria-label={`Восстановить «${p.name}»`}
                      title="Восстановить"
                    >
                      <ArchiveRestore size={12} />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => void handleArchive(e, p)}
                      className="p-0.5 rounded text-tertiary hover:text-red-400 transition-colors md:opacity-0 md:group-hover:opacity-100"
                      aria-label={`Архивировать «${p.name}»`}
                      title="В архив"
                    >
                      <Archive size={12} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
